import React from "react";
import { collectorDisplayName } from "../map/collectorIdentity";
import { tradeCardImages, tradeCardNames, idOf } from "./tradeUiLogic";
import { nextActionFor, statusLabel } from "./tradeJourneyLogic";
import TradeMiniProgress from "./TradeMiniProgress";
import { formatCash } from "./cashOffer";

/** Renders as a button only when it actually does something. */
const Box = ({ as: Tag = "div", children, ...rest }) => <Tag {...rest}>{children}</Tag>;

/**
 * TradeInboxCard — one trade, compact enough that six fit in a scroll and a half.
 *
 * Everything it needs comes from its OWN trade: its stage, its next action, its
 * busy and error state. There is no app-level "current trade" and no global busy
 * flag, so a failure on this card renders inside this card and leaves the other
 * five interactive.
 *
 * Loudness is earned: only an `act` tone gets the violet border and shadow, so a
 * screenful of trades has at most a few things shouting.
 */
const THUMB_W = 40;
const THUMB_H = 56;
const MAX_THUMBS = 3;

const Thumbs = ({ urls, names = [], label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
    {urls.slice(0, MAX_THUMBS).map((src, i) => (
      <img
        key={`${src}-${i}`}
        src={src}
        alt={names[i] || ""}
        loading="lazy"
        style={{
          width: THUMB_W,
          height: THUMB_H,
          objectFit: "cover",
          borderRadius: 5,
          border: "1px solid #e2e8f0",
          background: "#f1f5f9",
        }}
      />
    ))}
    {urls.length > MAX_THUMBS && (
      <span style={{ fontSize: 11, fontWeight: 800, color: "#64748b" }}>
        +{urls.length - MAX_THUMBS}
      </span>
    )}
    {urls.length === 0 && (
      <span style={{ fontSize: 11, color: "#64748b" }}>{label}</span>
    )}
  </div>
);

const TONE_BORDER = {
  act: "1.5px solid #6d28d9",
  wait: "1px solid #e2e8f0",
  done: "1px solid #e2e8f0",
  muted: "1px solid #e2e8f0",
};

const TradeInboxCard = ({
  trade,
  myId,
  busy = false,
  error = null,
  // The journey model names the next STEP ("Review offer"). Where the button
  // acts inline instead (Cancel, Confirm), the caller passes the label saying
  // what it really does. A button that says "review" and instead commits you to
  // a trade is precisely the dishonesty this redesign is removing — and since
  // Q-07 "Review offer" does what it says: it opens the offer, the one place
  // an offer is accepted.
  primaryLabel = null,
  // An unread update on this trade (P0 area C). The Trades tab badge counts
  // these; without a marker here the badge said 2 while the page showed
  // nothing new (skeptic, launch sprint).
  hasUpdate = false,
  onOpen,
  onPrimary,
  onSecondary,
}) => {
  // No presence options: whether this trade needs the in-person check-in is the
  // server's `presenceRequired`, which `GET /trades` now sends on every row. The
  // prop that used to feed the client's own copy of the rule was never passed by
  // any caller, so the rail and this button silently answered "not required" on
  // trades the journey page showed as gated (P2-42).
  const action = nextActionFor(trade, myId);
  const { mine, theirs } = tradeCardImages(trade, myId);
  const names = tradeCardNames(trade, myId);
  const say = (list) => list.filter(Boolean).join(", ");
  const swapLine = say(names.mine) && say(names.theirs) ? `${say(names.mine)} ⇄ ${say(names.theirs)}` : "";
  const iAmSender = idOf(trade.senderId) === String(myId);
  const other = iAmSender ? trade.receiverId : trade.senderId;
  const otherName = collectorDisplayName(other);
  const tone = action?.tone || "muted";
  // Show the cash that is actually IN the trade, labelled by direction.
  //
  // This used to be `iAmSender ? offeredCash : requestedCash`, and nothing in
  // the app ever writes requestedCash — TradeRequestModal has only offeredCash
  // state and sends only that. So for the RECEIVER it resolved to null and the
  // line never rendered: the person being offered money saw no sign of it while
  // triaging their inbox, and only found out after opening the trade. Cash is
  // the biggest factor in whether an offer is worth opening.
  //
  // Direction matters as much as the amount — an unlabelled figure on both sides
  // would be worse than the original bug, not better.
  const senderCash = trade.offeredCash;
  const receiverCash = trade.requestedCash;
  const cash = iAmSender ? senderCash : receiverCash;
  const theirCash = iAmSender ? receiverCash : senderCash;

  return (
    <div
      data-testid={`trade-inbox-card-${trade._id}`}
      data-tone={tone}
      style={{
        background: "#fff",
        border: TONE_BORDER[tone] || TONE_BORDER.muted,
        borderRadius: 14,
        padding: 14,
        boxShadow: tone === "act" ? "0 4px 16px rgba(109,40,217,0.13)" : "none",
        opacity: busy ? 0.72 : 1,
        transition: "opacity .15s ease",
      }}
    >
      {/* Header: who, and what this trade needs.
          Only a BUTTON when there is somewhere to go — the journey route lands
          in a follow-up, and a chevron that does nothing is exactly the kind of
          fake affordance this redesign exists to remove. */}
      <Box
        as={onOpen ? "button" : "div"}
        type={onOpen ? "button" : undefined}
        onClick={onOpen ? () => onOpen(trade) : undefined}
        data-testid={onOpen ? `trade-open-${trade._id}` : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          cursor: onOpen ? "pointer" : "default",
          textAlign: "left",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* The name shortens; the "New update" pill does not. It used to sit
              inside the name's ellipsis, so a long name hid it (E2E audit 18·15). */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              fontSize: 14,
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            <bdi
              data-testid={`trade-name-${trade._id}`}
              style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {otherName}
            </bdi>
            {hasUpdate && (
              <span
                data-testid={`trade-new-update-${trade._id}`}
                style={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  marginLeft: 8,
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: "#7c3aed",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  verticalAlign: "middle",
                }}
              >
                New update
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>
            {action?.hint || statusLabel(trade.status)}
          </div>
        </div>
        {onOpen && (
          <span style={{ color: "#cbd5e1", fontSize: 18, flexShrink: 0 }} aria-hidden="true">
            ›
          </span>
        )}
      </Box>

      {/* What is actually on the table, both sides */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <Thumbs urls={mine} names={names.mine} label="Nothing yet" />
        <span style={{ color: "#94a3b8", fontSize: 15, fontWeight: 800 }} aria-hidden="true">
          ⇄
        </span>
        <Thumbs urls={theirs} names={names.theirs} label="Nothing yet" />
      </div>
      {/* The same cards in words: two trades with one collector otherwise
          look identical, and a thumbnail alone does not say which card. */}
      {swapLine && (
        <div
          data-testid={`trade-swap-line-${trade._id}`}
          style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {swapLine}
        </div>
      )}

      {(cash || theirCash) && (
        <div
          data-testid={`trade-cash-${trade._id}`}
          style={{ marginTop: 8, fontSize: 11.5, fontWeight: 800, color: "#6d28d9" }}
        >
          {cash && <span data-testid={`trade-cash-you-${trade._id}`}>You add {formatCash(cash)}</span>}
          {cash && theirCash && <span> · </span>}
          {theirCash && (
            <span data-testid={`trade-cash-them-${trade._id}`}>They add {formatCash(theirCash)}</span>
          )}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <TradeMiniProgress trade={trade} myId={myId} />
      </div>

      {/* This card's own error — never a page-level banner, so the other five
          cards stay usable when this one fails. */}
      {error && (
        <div
          data-testid={`trade-error-${trade._id}`}
          role="alert"
          style={{
            marginTop: 10,
            padding: "7px 10px",
            borderRadius: 9,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: 11.5,
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      )}

      {action?.label && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            data-testid={`trade-primary-${trade._id}`}
            disabled={busy}
            onClick={() => onPrimary && onPrimary(trade, action)}
            style={{
              flex: 1,
              padding: "10px 12px",
              borderRadius: 10,
              border: "none",
              cursor: busy ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 800,
              color: tone === "act" ? "#fff" : "#0f172a",
              background: tone === "act" ? "#6d28d9" : "#f1f5f9",
            }}
          >
            {busy ? "Working…" : primaryLabel || action.label}
          </button>
          {onSecondary && ["respond", "promised", "blocked_offer"].includes(action.key) && (
            <button
              type="button"
              data-testid={`trade-secondary-${trade._id}`}
              disabled={busy}
              onClick={() => onSecondary(trade, action)}
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "#fff",
                cursor: busy ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 800,
                color: "#64748b",
              }}
            >
              Decline
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TradeInboxCard;
